import union from '@turf/union'
import jsts from 'jsts'

const reject = (code) => {
  const error = new Error(code)
  error.code = code
  throw error
}

const validatedGeometry = (value) => {
  const geometry = value?.type === 'Feature' ? value.geometry : value
  if (!geometry || !['Polygon', 'MultiPolygon'].includes(geometry.type)) {
    reject('invalid_land_geometry')
  }
  const polygons = geometry.type === 'Polygon' ? [geometry.coordinates] : geometry.coordinates
  if (!Array.isArray(polygons) || polygons.length === 0) reject('invalid_land_geometry')
  for (const rings of polygons) {
    if (!Array.isArray(rings) || rings.length === 0) reject('invalid_land_geometry')
    for (const ring of rings) {
      if (!Array.isArray(ring) || ring.length < 4) reject('invalid_land_geometry')
      for (const point of ring) {
        if (!Array.isArray(point) || point.length !== 2 || !point.every(Number.isFinite)
          || Math.abs(point[0]) > 180 || Math.abs(point[1]) > 90) reject('invalid_land_geometry')
      }
      if (ring[0][0] !== ring[ring.length - 1][0] || ring[0][1] !== ring[ring.length - 1][1]) {
        reject('invalid_land_geometry')
      }
    }
  }
  // The installed JSTS validator (already a dependency of the existing Turf
  // packages) rejects self intersections, external/nested holes and overlapping
  // MultiPolygon interiors. Never buffer(0), simplify, close rings or repair.
  try {
    const shape = new jsts.io.GeoJSONReader().read(geometry)
    if (shape.isEmpty() || !new jsts.operation.valid.IsValidOp(shape).isValid()) {
      reject('invalid_land_geometry')
    }
  } catch (error) {
    reject('invalid_land_geometry')
  }
  return {type: geometry.type, coordinates: JSON.parse(JSON.stringify(geometry.coordinates))}
}

// Exact planar set union in the supplied WGS84 coordinates. This creates only
// an unsaved field draft: no convex envelope, buffer, gap filling or area cap.
// Explicit inactive records are excluded; missing active flags are the normal
// active-land DTO. An invalid active land fails the WHOLE operation.
export const fieldPerimeterFromLands = (lands) => {
  if (!Array.isArray(lands)) reject('no_active_lands')
  const active = lands.filter((land) => land?.active !== false && land?.active !== 0)
  if (active.length === 0) reject('no_active_lands')
  const features = active.map((land) => {
    if (!land || land.gap_id != null || (land.active !== undefined && land.active !== true && land.active !== 1)
      || (land.resource_type !== undefined && land.resource_type !== 'land')) {
      reject('invalid_land_geometry')
    }
    return {type: 'Feature', properties: {}, geometry: validatedGeometry(land.polygons ?? land.polygon)}
  })
  let result = features[0]
  try {
    for (const feature of features.slice(1)) result = union(result, feature)
  } catch (error) {
    reject('union_failed')
  }
  try {
    return validatedGeometry(result?.geometry)
  } catch (error) {
    reject('union_failed')
  }
}
